/* tslint:disable:import-spacing no-inferrable-types variable-name no-trailing-whitespace */
import {
  TrainClass,
  TrainDirection
}                      from './train.class';
import { Penalty }     from './penalty';
import { Connection }  from './connection';
import {
  Track,
  TrackType
}                      from './track';
import { InterLink }   from './inter.link';

export enum DecisionPointKind {
  NULL,
  BEGIN,
  INTERNAL,
  END
}

export enum SwitchingLayout {
  AllCross,
  NoCross,
  StraightThrough,
  LeftHandCross,
  RightHandCross
}

export enum JunctionLayout {
  AllJunction,
  EastJunction,
  WestJunction,
  NoJunction
}

/**
 *
 * @class DecisionPoint
 *
 * @brief a place between tracks where switching can happen, allowing trains to move
 * from one track to another.   The rail network is basically decision points with
 * bundles of track between each pair of decision points.  Normally the "corridor" is
 * bounded by an east side and west side terminal track (each with no outer decision
 * point).
 *
 * Normally mileage is increasing to the west, the easternmost decision point is mile
 * 0.  Also, the viewing convention is that the east is on the right, and west (highest
 * mileage) is on the left.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2019
 *
 */
export class DecisionPoint {

  /** @var number unique id */
  public           _id: number = -1;

  /** @var string human readable label */
  public         _name: string = '';

  /** @var number the mile post value */
  public      _mileage: number = 0.0;

  /** @var number the 0 based index of this decision point */
  public          _nth: number = -1;

  /** @var number the west/east ordering of the decision point */
  public        _order: number = -1;

  /** @var DecisionPointKind the kind of decision point */
  public         _type: DecisionPointKind = DecisionPointKind.NULL;

  /** @var InterLink|null the west side (increasing mileage interlink) */
  public     _westSide: InterLink|null = null;

  /** @var InterLink|null the east side (decreasing mileage interlink) */
  public     _eastSide: InterLink|null = null;

  /** @var Map<number, Map<number, Penalty>>  the time penalties indexed [train class][speed] */
  public    _penalties: Map<number, Map<number, Penalty>> = new Map<number, Map<number, Penalty>>();

  /** @var Map<number, Connection> the set of routes that pass through this decision point */
  public  _connections: Map<number, Connection> = new Map<number, Connection>();

  /**  @var boolean flag for decision points that connect to a junction or a terminal */
  public _hasTermJunc: boolean = false;

  /** flag for decision points that we've cloned to fake nodes */
  public _isFake: boolean = false;

  /** @brief standard constructor */
  constructor(id: number, name: string, mileage: number, nth: number) {

    this._id      = id;
    this._name    = name;
    this._mileage = mileage;
    this._nth     = nth;

  }

  /** provide a quick shallow copy */
  public  clone(): DecisionPoint {

    /* we can't use Object.assign() as it won't copy the methods */

    const dp: DecisionPoint = new DecisionPoint(this._id, this._name, this._mileage, this._nth);

    dp._order       = this._order;
    dp._type        = this._type;
    dp._westSide    = this._westSide;
    dp._eastSide    = this._eastSide;
    dp._penalties   = this._penalties;
    dp._connections = this._connections;
    dp._hasTermJunc = this._hasTermJunc;

    /* pass it back */

    return dp;
  }


  /** check if this track is on the east (lower mileage) side of decision point */
  public isEastSideTrack(track: Track): boolean {

    if ((this.eastTracks() === null) || (this.eastTracks().length === 0)) {

      return false;
    }

    return this.eastTracks().some(x => x._id === track._id);
  }

  /** check if this track is on the east (lower mileage) side of decision point */
  public isWestSideTrack(track: Track): boolean {

    if ((this.westTracks() === null) || (this.westTracks().length === 0)) {

      return false;
    }

    return this.westTracks().some(x => x._id === track._id);
  }

  /** fetch the eastbound tracks through this decision point */
  eastTracks(): Track[] {

    if (!Array.isArray(this._eastSide)) {

      return this._westSide?._tracks ?? [];
    }

    return this._eastSide._tracks;
  }

  /** fetch the eastbound tracks through this decision point */
  public westTracks(): Track[] {

    if (!Array.isArray(this._westSide)) {

      return this._eastSide?._tracks ?? [];
    }

    return this._westSide._tracks;
  }

  /** fetch the tracks leaving this decision point, in the given direction */
  public incomingTracks(dir: TrainDirection): Track[] {

    if (dir === TrainDirection.EAST) {

      return this.westTracks();
    }

    return this.eastTracks();
  }

  /** fetch the decision points on either side (will only be 1 at either end) */
  public neighbours(): DecisionPoint[] {

    const focus: DecisionPoint   = this;
    const   dps: DecisionPoint[] = [];

    this._connections.forEach((c: Connection): void => {

      if (dps.length === 2) {

        /*
         * we currently only have corridor type networks, so there can be at most two neighbours,
         * one on either side in the chain.
         *
         */

        return ;
      }

      [
        c._to?._after,
        c._to?._before,
        c._from?._after,
        c._from?._before

      ].forEach( (dp: DecisionPoint|null|undefined): void => {

        if (!dp) {

          return ;
        }

        if (dp._id === focus._id) {

          /* self isn't a neighbour :) */

          return ;
        }


        if (dps.map(x => x._id).includes(dp._id)) {

          /* its already in the list */

          return ;
        }

        /* found a neighbour */

        dps.push(dp);


      });

    });

    /* if we have both neighbours, order them so that west is first least is last */

    if (dps.length === 2) {

      if (dps[0]._mileage < dps[1]._mileage) {

        const tmp: DecisionPoint = dps[0];
        dps[0] = dps[1];
        dps[1] = tmp;
      }
    }


    return dps;
  }

  /** get the previous neighboring decision point */
  public next(dir: TrainDirection): DecisionPoint|null {

    /* use the connections to find the decision points on either side of this one */

    const dps: DecisionPoint[] = this.neighbours();

    if (dir == TrainDirection.EAST) {

      /* going lower in mileage */

      if (dps.length === 1) {

        if (dps[0]._mileage > this._mileage) {

          /* we are the east most dp */

          return null;
        }
      }

      return dps.pop() ?? null;
    }

    /* going higher in mileage */

    if (dps.length === 1) {

      if (dps[0]._mileage < this._mileage) {

        /* we are the west most dp */

        return null;
      }
    }

    return dps.shift() ?? null;
  }

  /** get the next neighboring decision point */
  public previous(dir: TrainDirection): DecisionPoint|null {

    /* use the connections to find the decision points on either side of this one */

    const dps: DecisionPoint[] = this.neighbours();

    if (dir == TrainDirection.EAST) {

      /* going higher in mileage */

      if (dps.length === 1) {

        if (dps[0]._mileage < this._mileage) {

          /* we are the west most dp */

          return null;
        }
      }

      return dps.shift() ?? null;
    }

    /* going lower in mileage */

    if (dps.length === 1) {

      if (dps[0]._mileage > this._mileage) {

        /* we are the east most dp */

        return null;
      }
    }

    return dps.pop() ?? null;
  }

  /** this version of outgoingTracks() includes all possible junctions and terminals from both sides. */
  public outgoingTracksJT(dir: TrainDirection): Track[] {

    /* first get the normal directional result */

    let output: Track[];
    let otherSide: Track[];

    if (dir === TrainDirection.EAST) {

      output    = this.eastTracks();
      otherSide = this.westTracks();

    } else {

      output    = this.westTracks();
      otherSide = this.eastTracks()
    }

    /* now merge in any junction or terminal from the other side that we don't already have by name */

    /* do we already have this track by ID (node)? */

    for (const otherTrack of otherSide) {

      if (output.find((x: Track): boolean => x._id == otherTrack._id) ?? null) {

        /* already have this track by ID */

        continue;
      }

      /* do we already have this track by name? */

      const otherName: string = Track.typeToString(otherTrack._type) + otherTrack._track;
      let found: boolean      = false;

      for (const track of output) {

        const name: string = Track.typeToString(track._type) + track._track;

        if (name == otherName) {

          found = true;

          break;
        }

      }

      if (found) {

        /* already have this track by Name */

        continue;
      }

      /*
       * ok, its unique / different from what we already have, if it's a junction or terminal track
       * than we want to merge it in.
       *
       */

      if (otherTrack._type == TrackType.JUNCTION || otherTrack._type == TrackType.TERMINAL) {

        output.push(otherTrack);
      }

    } /* end of looking for merge junctions/terminals */

    /* pass back the results */

    return output;
  }

  /** fetch the tracks leaving this decision point, in the given direction */
  public outgoingTracks(dir: TrainDirection): Track[] {

    if (dir === TrainDirection.EAST) {

      return this.eastTracks();
    }

    return this.westTracks();
  }

  /** @brief register the delays for a given train class and speed */
  public setPenalty(tc: TrainClass, speed: number, delay: Penalty) {

    if (!this._penalties.has(tc._id)) {

      this._penalties.set(tc._id, new Map<number, Penalty>());
    }

    const item: Map<number, Penalty>|null = this._penalties.get(tc._id) ?? null;

    if (item) {

      item.set(speed, delay);
    }
  }

  /** @brief help to just dump as a string */
  public asString(): string {

    return `${this._name.trim()} ${this._mileage.toFixed(2)} mi`;
  }

  /** @brief fetch the delays for the given train class and speed. */
  public penalty(tc: TrainClass, speed: number): Penalty|null {

    if (!this._penalties.has(tc._id)) {

      return null;
    }

    const item: Map<number, Penalty>|null = this._penalties.get(tc._id) ?? null;

    if (!item) {

      return null;
    }

    if (!item.has(speed)) {

      return null;
    }

    return item.get(speed) ?? null;
  }

  /** @brief fetch the junction layout at this decision point */
  public junctionLayout(): JunctionLayout {

    /*
     *
     * Junction connections:
     *
     *   J_X junction to everything
     *   J_E_0 Junction to the east, track 0 (could be a P0 or an M0)
     *   J_W_1 junction to the west, track 1 (could be P1 or M1)
     *
     */

    let east: boolean = false;
    let west: boolean = false;

    for (const c of this._connections.values()) {

      if ((c._from && c._from._type !== TrackType.JUNCTION) && (c._to && c._to._type !== TrackType.JUNCTION)) {

        continue;
      }

      /* what kind of connection do we have at least one of? */

      let dst: Track|null = c._from;

      if (dst && dst._type === TrackType.JUNCTION) {

        dst = c._to;
      }

      if (dst && dst._track === 0) {

        east = true;
      }

      if (dst && dst._track === 1) {

        west = true;
      }

    }

    if (east && west) {

      return JunctionLayout.AllJunction;
    }

    if (east) {

      return JunctionLayout.EastJunction;
    }

    if (west) {

      return JunctionLayout.WestJunction;
    }

    return JunctionLayout.NoJunction;
  }

  /** helper to fetch any junction or terminal tracks on either side of this decision point */
  public diversionTracks(): Track[] {

    /* only keep the tracks (either side) which are terminal or junction */

    let lhs: (Track|null)[] = Array.from(this._connections.values())
      .map( (c: Connection) => c._from)
      .filter( (t: Track|null) =>
        [TrackType.JUNCTION, TrackType.TERMINAL].includes(t ? t._type : TrackType.UNKNOWN)
      );

    lhs = lhs.filter((x: Track|null, index: number) => {

      const first: number = lhs.findIndex( (y: Track|null): boolean => {

        if (!y || !x) {

          return false;
        }

        return y._id === x._id;

      });

      return !((first < index) && (first >= 0));
    });

    lhs.reduce( (a: Track[], b: Track|null): Track[] => {

        if (!a || !b) {

          return [];
        }

        if (a.indexOf(b)<0) {

          a.push(b);
        }

        return a;
      },
      []
    );

    let rhs: (Track|null)[] = Array.from(this._connections.values())
      .map( (c: Connection) => c._to)
      .filter( (t: Track|null) =>
        [TrackType.JUNCTION, TrackType.TERMINAL].includes(t ? t._type : TrackType.UNKNOWN)
      );

    rhs = rhs.filter( (x: Track|null, index: number) => {

      const first: number = lhs.findIndex( (y: Track|null): boolean => {

        if (!y || !x) {

          return false;
        }

        return y._id === x._id
      });

      return !((first < index) && (first >= 0));
    });

    lhs.sort((a: Track|null, b: Track|null): number => {

      if (!a || !b) {

        return 0;
      }

      return a._track - b._track;
    });

    rhs.sort((a: Track|null, b: Track|null) => {

      if (!a || !b) {

        return 0;
      }

      return a._track - b._track;
    });

    /* pass 'em back */

    let results: Track[] = [];

    lhs.forEach( (x: Track|null): void => {

      x && results.push(x);
    })

    rhs.forEach( (x: Track|null): void => {

      x && results.push(x);
    });

    results = results.filter((value: Track, index: number, array: Track[]) => {
      return array.indexOf(value) === index;
    });

    return results;
  }

  /**
   * @brief fetch a summary of the switching layout at this decision point, you have to
   * provide your arrival track.
   */
  public switchLayout(from: Track): SwitchingLayout {

    /*
     * walk through the connections looking for the conditions we need to match
     * one of the known layouts.
     *
     *    X = All crossovers (M1-M0,M0-M1,M0-M0,M1-M1)
     *    Z = No crossovers (M1-M1, M0-M0)
     *   LH = Left hand only(M0-M0,M1-M1,M1-M0) - this assumes that M0 is the "south" track
     *   RH = Right hand only (M0-M0,M1-M1,M0-M1) - this assumes that M0 is the "south" track
     *
     * Connections themselves don't have a direction though, that depends on the train's
     * direction of travel, on which link it arrived from to get to this decision point.  We
     * can use the interlink index of the 'from' track to know which tracks (to or from), are on
     * the arriving side.  We can then use the "other" side from that as the direction of
     * travel.
     *
     */

    let  leftHandCross: boolean = false;
    let rightHandCross: boolean = false;
    let  southStraight: boolean = false;
    let  northStraight: boolean = false;

    /*
     * each connection has a to and a from track, connections don't tell us direction,
     * but one of the 'to' / 'from' tracks will have the same interlink as the one we
     * arrived at, so that is the source, we then use the other one as the destination.
     *
     */

    for (const c of this._connections.values()) {

      if (!c._from || !c._to) {

        /* incomplete connection? */

        continue;
      }

      if ((c._from._type === TrackType.JUNCTION) || (c._to._type === TrackType.JUNCTION)) {

        continue ;
      }

      /* orient ourselves to move through this switching point, continuing our line of travel */

      let src: Track|null = null;
      let dst: Track|null = null;

      if (from == null) {

        /* just assume eastward bound train, graphs are normally given in eastbound direction */

        if ((c._from._peers && c._to._peers) && (c._from._peers._sIndex > c._to._peers._sIndex)) {

          src = c._to;
          dst = c._from;

        } else {

          src = c._from;
          dst = c._to;
        }

      } else {

        if ((c._from._peers && from._peers) && (c._from._peers._sIndex === from._peers._sIndex)) {

          src = c._from;
          dst = c._to;

        } else {

          src = c._to;
          dst = c._from;
        }
      }

      /* which type of crossing did we find at least one of? */

      if ((src._track === 0) && (dst._track === 0)) {

        southStraight = true;
      }

      if ((src._track === 1) && (dst._track === 1)) {

        northStraight = true;
      }

      if ((src._track === 0) && (dst._track === 1)) {

        rightHandCross = true;
      }

      if ((src._track === 1) && (dst._track === 0)) {

        leftHandCross = true;
      }
    }

    /* summarize ... */

    if (leftHandCross && rightHandCross && southStraight && northStraight) {

      return SwitchingLayout.AllCross;
    }

    if (!leftHandCross && !rightHandCross) {

      return SwitchingLayout.NoCross;
    }

    if ((!leftHandCross && !rightHandCross) && (southStraight || northStraight)) {

      return SwitchingLayout.StraightThrough;
    }

    if (leftHandCross) {

      return SwitchingLayout.LeftHandCross;
    }

    if (rightHandCross) {

      return SwitchingLayout.RightHandCross;
    }

    return SwitchingLayout.StraightThrough;
  }


}
